import { useQuery } from '@tanstack/react-query';
import type { TerminologyCoding, ConceptMatchView } from '@wodi/shared';
import { CodedValuePicker, type CodedValueOption, type SearchPickerStatus } from '@wodi/ui';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { recordRemoteSelection, searchTerminology } from '../api/terminology.ts';
import { useOnline } from '../hooks/use-online.ts';

// The coded-value picker contract (plan §6.1) for diagnosis, lab test and service
// pickers. Search text stays in component state and the POST body; never in the URL.

/** A shorter pause than patient search: a picker fires on every word typed. */
export const TERMINOLOGY_DEBOUNCE_MS = 150;
const MIN_CHARS = 2;

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

function toOption(m: ConceptMatchView): CodedValueOption {
  return {
    key: m.conceptId ?? `${m.system}|${m.version}|${m.code}`,
    code: m.code,
    display: m.display,
    matchedTerm: m.matchedTerm,
    parentDisplay: m.parentDisplay,
  };
}

export interface TerminologyPickerProps {
  label: string;
  description?: string;
  /** Code system keys to search, e.g. ['icd11_mms']. */
  systems: readonly string[];
  /** Ask the national service when nothing matches locally. Default true. */
  includeNational?: boolean;
  /** Called with what the record must store: system, version, code, display, source. */
  onSelect: (coding: TerminologyCoding) => void;
  autoFocus?: boolean;
}

export function TerminologyPicker({
  label,
  description,
  systems,
  includeNational = true,
  onSelect,
  autoFocus,
}: TerminologyPickerProps) {
  const online = useOnline();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const debounced = useDebounced(trimmed, TERMINOLOGY_DEBOUNCE_MS);
  const search = useQuery({
    queryKey: ['terminology-search', systems.join(','), includeNational, debounced],
    queryFn: () => searchTerminology({ systems: [...systems], query: debounced, includeNational }),
    enabled: online && debounced.length >= MIN_CHARS,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: false,
  });

  let status: SearchPickerStatus;
  if (trimmed.length < MIN_CHARS) status = 'idle';
  else if (!online) status = 'offline';
  else if (debounced !== trimmed || search.isFetching || search.isPending) status = 'searching';
  else if (search.isError) status = 'error';
  else status = 'ready';

  const data = search.data;
  const byKey = new Map<string, ConceptMatchView>();
  for (const m of [...(data?.local ?? []), ...(data?.national.items ?? [])]) {
    byKey.set(toOption(m).key, m);
  }
  const error = search.error;
  const errorMessage =
    error instanceof ApiError && error.code === 'no_active_version'
      ? `${label} codes aren't set up yet. Ask your administrator.`
      : error instanceof ApiError
        ? error.message
        : 'Search did not work. Try again.';

  return (
    <CodedValuePicker
      label={label}
      {...(description === undefined ? {} : { description })}
      inputValue={text}
      onInputChange={setText}
      status={status}
      itemsQuery={debounced}
      local={(data?.local ?? []).map(toOption)}
      national={{
        state: data?.national.state ?? 'not_needed',
        items: (data?.national.items ?? []).map(toOption),
      }}
      onSelect={(option, source) => {
        const match = byKey.get(option.key);
        if (match === undefined) return;
        const coding: TerminologyCoding = {
          system: match.system,
          version: match.version,
          code: match.code,
          display: match.display,
          source,
        };
        if (source === 'national_service') {
          // Counted so the reference data team can see what the cache is missing.
          void recordRemoteSelection(coding).catch(() => undefined);
        }
        onSelect(coding);
      }}
      errorMessage={errorMessage}
      onRetry={() => {
        void search.refetch();
      }}
      {...(autoFocus === undefined ? {} : { autoFocus })}
    />
  );
}
